import { motion } from "framer-motion";
import { Star, ExternalLink } from "lucide-react";
import { useRef, useState, useEffect } from "react";

const GMB_LINK = "https://maps.app.goo.gl/ZBNHFw3Qe1MadnTM6";

const reviews = [
  {
    name: "J J",
    rating: 5,
    time: "7 years ago",
    text: "I ordered for diaries for gifting purpose, with my company name and product brochure page. It was delivered with great quality, neatly packed and was on time. Looking forward to buying again.",
    color: "#4285F4",
  },
  {
    name: "Mohammed Malik",
    rating: 5,
    time: "4 months ago",
    text: "Best quality diaries and professional. They take bulk orders and name printing as well.",
    color: "#EA4335",
  },
  {
    name: "Kiran Kumar S",
    rating: 5,
    time: "4 years ago",
    text: "Awesome diaries with better pricing and all printing, embossing, engraving work done here!",
    color: "#34A853",
  },
  {
    name: "Chandan G",
    rating: 5,
    time: "a year ago",
    text: "Best new year diaries in Bangalore for corporate gifting, reasonable pricing.",
    color: "#FBBC05",
  },
  {
    name: "Maruthi Prasad",
    rating: 4,
    time: "5 years ago",
    text: "Best quality to buy professional diaries.",
    color: "#4285F4",
  },
  {
    name: "Selvaraj Raj",
    rating: 5,
    time: "9 years ago",
    text: "Approach for world class Eagle Diaries.",
    color: "#EA4335",
  },
  {
    name: "Rahul P R",
    rating: 5,
    time: "a year ago",
    text: "Good quality products.",
    color: "#34A853",
  },
  {
    name: "Krishna Chandran",
    rating: 5,
    time: "6 years ago",
    text: "Good service.",
    color: "#FBBC05",
  },
];

const Stars = ({ count }) => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-3.5 h-3.5 ${i < count ? "text-[#FBBC05] fill-[#FBBC05]" : "text-white/20"}`}
      />
    ))}
  </div>
);

const ReviewCard = ({ review, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.06 }}
    className="min-w-[300px] sm:min-w-[340px] snap-start"
    data-testid={`review-card-${index}`}
  >
    <div className="h-full bg-white/[0.07] backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/[0.12] transition-colors duration-300">
      <div className="flex items-start gap-3.5 mb-4">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
          style={{ backgroundColor: review.color, fontFamily: "'Outfit', sans-serif" }}
        >
          {review.name.charAt(0)}
        </div>
        <div className="min-w-0">
          <h4
            className="text-white text-sm font-medium truncate"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {review.name}
          </h4>
          <div className="flex items-center gap-2 mt-0.5">
            <Stars count={review.rating} />
            <span
              className="text-[11px] text-white/40"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {review.time}
            </span>
          </div>
        </div>
      </div>
      <p
        className="text-sm text-white/70 leading-relaxed"
        style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
      >
        "{review.text}"
      </p>
      <div className="mt-4 flex items-center gap-1.5">
        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" aria-hidden="true">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        <span className="text-[10px] text-white/30" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Google Review
        </span>
      </div>
    </div>
  </motion.div>
);

export const ReviewsSection = () => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.addEventListener("scroll", checkScroll, { passive: true });
      checkScroll();
      return () => el.removeEventListener("scroll", checkScroll);
    }
  }, []);

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 360, behavior: "smooth" });
  };

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative py-24 sm:py-32 bg-[#7A1A31] overflow-hidden"
    >
      {/* Decorative orbs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-white/[0.03] blur-3xl" aria-hidden="true" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-[#C8A23A]/10 blur-3xl" aria-hidden="true" />

      <div className="container mx-auto px-6 sm:px-8 max-w-7xl relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8 mb-12"
        >
          <div>
            <span
              className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C8A23A] mb-4 block"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Customer Reviews
            </span>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="reviews-heading"
            >
              Trusted by <span className="text-[#C8A23A]">Businesses</span>
            </h2>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className={`w-5 h-5 ${i < 5 ? "text-[#FBBC05] fill-[#FBBC05]" : "text-white/20"}`} />
                ))}
              </div>
              <span className="text-white text-lg font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                4.6
              </span>
              <span className="text-white/50 text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>
                on Google
              </span>
            </div>
          </div>

          {/* Scroll arrows + CTA */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => scroll(-1)}
              disabled={!canScrollLeft}
              data-testid="reviews-scroll-left"
              className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-all disabled:opacity-20 disabled:cursor-not-allowed"
              aria-label="Scroll left"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => scroll(1)}
              disabled={!canScrollRight}
              data-testid="reviews-scroll-right"
              className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-all disabled:opacity-20 disabled:cursor-not-allowed"
              aria-label="Scroll right"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </motion.div>

        {/* Scrollable review cards */}
        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4 -mx-6 px-6"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          data-testid="reviews-carousel"
        >
          {reviews.map((review, i) => (
            <ReviewCard key={i} review={review} index={i} />
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-10 text-center"
        >
          <a
            href={GMB_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gmb-review-link"
            className="inline-flex items-center gap-3 bg-white text-[#7A1A31] px-8 py-4 rounded-full hover:bg-[#C8A23A] hover:text-white transition-all duration-300 text-sm font-semibold shadow-lg"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Read All Reviews on Google
            <ExternalLink className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
